import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PersonaReviewItem } from '../types';
import { getConversationTitle, getScoreSummary } from '../utils';
import { PersonaContext } from './PersonaContext';
import { ConversationTranscript } from './ConversationTranscript';
import { EvaluatorResults } from './EvaluatorResults';
import { RunStatusBadge, ScoreBadge } from './status';

export function ConversationReview({
  persona,
  selectedConversationId,
  onSelectConversation,
}: {
  persona: PersonaReviewItem | null;
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
}) {
  const conversation = persona?.conversations.find((item) => item.id === selectedConversationId)
    ?? persona?.conversations[0]
    ?? null;
  const score = conversation ? getScoreSummary(conversation.scores) : null;
  const [activeReviewTab, setActiveReviewTab] = useState<'transcript' | 'evaluation'>('transcript');

  useEffect(() => {
    setActiveReviewTab('transcript');
  }, [conversation?.id]);

  if (!persona || !conversation) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-sm text-muted-foreground">
        Select a persona to review its generated conversation.
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="border-b px-5 py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold leading-6 text-foreground">{persona.label}</h2>
              <RunStatusBadge status={conversation.status} />
              <ScoreBadge score={score?.average ?? null} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {[persona.title, persona.company, persona.population].filter(Boolean).join(' · ')}
            </p>
          </div>
          {persona.conversations.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {persona.conversations.map((item, index) => (
                <Button
                  key={item.id}
                  type="button"
                  size="sm"
                  variant={item.id === conversation.id ? 'default' : 'outline'}
                  onClick={() => onSelectConversation(item.id)}
                >
                  {getConversationTitle(item, index)}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-4">
          <PersonaContext persona={persona} />
        </div>
      </div>
      <Tabs
        value={activeReviewTab}
        onValueChange={(value) => setActiveReviewTab(value as 'transcript' | 'evaluation')}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="border-b px-5 py-3">
          <TabsList className="h-8 w-fit rounded-lg border border-border/80 bg-muted p-1">
            <TabsTrigger value="transcript" className="h-6 rounded-md px-3 text-[12px]">
              Simulation
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="h-6 rounded-md px-3 text-[12px]">
              Performance
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="transcript" className="mt-0 min-h-0 flex-1 overflow-auto px-5 py-5">
          <ConversationTranscript conversation={conversation} />
        </TabsContent>
        <TabsContent value="evaluation" className="mt-0 min-h-0 flex-1 overflow-auto px-5 py-5">
          <EvaluatorResults score={score} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
